<!-- @format -->
<template>
    <div class="marketing-plan-template-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Marketing Plan Template Settings</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div class="marketing-plan-template-name-container">
                        <app-input v-model="marketingPlanTemplate.name" label="Name" />
                    </div>
                    <div class="marketing-plan-template-category-container">
                        <global-category-dropdown
                            :account-id="accountId"
                            show-selected-up
                            :selected="marketingPlanTemplate.category"
                            @input="addListCategories($event)"
                            @remove="deleteCategory"
                        />
                    </div>
                    <div class="marketing-plan-template-description-container">
                        <app-text-area
                            v-model="marketingPlanTemplate.description"
                            label="Description"
                        />
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div v-if="marketingPlanTemplate.id" class="action-container">
                    <app-button class="next-btn" @click="saveAndClose()">
                        <span>Save & Close</span>
                    </app-button>
                    <app-button
                        v-if="!isCalendarView"
                        class="next-btn"
                        @click="saveAndViewCalendar()"
                    >
                        <span>Save & View Calendar</span>
                    </app-button>
                </div>
                <div v-else class="action-container">
                    <app-button
                        v-if="!isCalendarView"
                        class="next-btn"
                        @click="saveAndViewCalendar()"
                    >
                        <span>Next Step</span>
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const AppTextArea = () => import('@/components/common/app-textarea');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const GlobalCategoryDropdown = () => import('@/components/common/global-category-dropdown');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'marketing-plan-template-widget',

    components: {
        'app-button': AppButton,
        'app-text-area': AppTextArea,
        'app-input': AppInput,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'global-category-dropdown': GlobalCategoryDropdown,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            clientId: this.widget.clientId,
            agencyId: this.widget.agencyId,
            isCalendarView: false,
            marketingPlanTemplate: {
                id: this.widget.marketingPlanTemplateId,
                name: null,
                description: null,
                category: [],
            },
        };
    },

    computed: {
        accountId() {
            return this.widget.accountId;
        },

        isFromStudio() {
            return this.widget.options ? this.widget.options.isFromStudio : false;
        },
    },

    created() {
        if (this.marketingPlanTemplate.id) {
            this.getMarketingPlanTemplate();
        }

        if (this.$route.path.includes('/Calendar')) {
            this.isCalendarView = true;
        }
    },

    methods: {
        addListCategories(item) {
            const exist = this.marketingPlanTemplate.category.some(
                (element) => element === item.name
            );

            if (!exist) {
                this.marketingPlanTemplate.category.push(item.name);
            }
        },

        deleteCategory(item) {
            this.marketingPlanTemplate.category.splice(
                this.marketingPlanTemplate.category.indexOf(item),
                1
            );
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        saveAndViewCalendar() {
            if (this.marketingPlanTemplate.id) {
                this.patch().then(() => {
                    EventBus.$emit('marketing-plan-template:update');
                    this.viewCalendar();
                });
            } else {
                this.post().then(this.viewCalendar);
            }
        },

        saveAndClose() {
            if (this.marketingPlanTemplate.id) {
                this.patch().then(() => {
                    EventBus.$emit('marketing-plan-template:update');
                    EventBus.$emit('my-creations:update');
                    this.closeWidget();
                });
            } else {
                this.post().then(this.closeWidget);
            }
        },

        post() {
            this.isLoading = true;

            return new Promise((resolve, reject) => {
                var request = {
                    Name: this.marketingPlanTemplate.name,
                    Description: this.marketingPlanTemplate.description,
                    Categories: this.marketingPlanTemplate.category,
                    ClientId: this.clientId,
                    AgencyId: this.agencyId,
                };

                this.$api
                    .post('/api/MarketingPlanTemplates', request)
                    .then((response) => {
                        this.marketingPlanTemplate.id = response.data.Id;
                        this.$helpers.showSuccessNotification(
                            'Marketing Plan Template saved successfully.'
                        );
                        EventBus.$emit('marketing-plan-template:update');
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('This template could not be saved.', { error });
                        reject();
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },

        patch() {
            this.isLoading = true;
            var changes = [
                {
                    name: 'Name',
                    value: this.marketingPlanTemplate.name,
                },
                {
                    name: 'Description',
                    value: this.marketingPlanTemplate.description,
                },
                {
                    name: 'Categories',
                    value: this.marketingPlanTemplate.category,
                },
            ];

            var url = `/api/MarketingPlanTemplates/${this.marketingPlanTemplate.id}`;
            return this.$api.patch(url, changes);
        },

        viewCalendar() {
            const dest =
                `/MarketingPlanTemplates/${this.marketingPlanTemplate.id}/Calendar` +
                `${this.isFromStudio ? '?fromStudio=true' : ''}`;

            this.closeWidget();
            this.$router.push(dest);
        },

        getMarketingPlanTemplate() {
            this.isLoading = true;
            var select = '$select=Name,Description';
            var expand = '$expand=Categories';
            var url = `/service/MarketingPlanTemplates?$filter=Id eq ${this.marketingPlanTemplate.id}&${expand}&${select}`;

            this.$api
                .get(url)
                .then((response) => {
                    var responseValue = response.data.value[0];

                    this.marketingPlanTemplate = {
                        id: this.marketingPlanTemplate.id,
                        name: responseValue.Name,
                        description: responseValue.Description,
                        category: responseValue.Categories.map(({ Name }) => Name),
                    };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Error retrieving template', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style>
.marketing-plan-template-widget .loading-screen {
    text-align: center;
}

.marketing-plan-template-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.marketing-plan-template-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.marketing-plan-template-widget .marketing-plan-template-name-container,
.marketing-plan-template-widget .marketing-plan-template-description-container,
.marketing-plan-template-widget .marketing-plan-template-category-container {
    margin-bottom: 23px;
}

.marketing-plan-template-widget .action-container {
    display: flex;
}

.marketing-plan-template-widget .action-container button {
    margin-right: 5px;
}
</style>
